/**
 * 通話料(アジャストワンの請求明細CSV)の読み取り(2026-09-24 発注者指示。P-67③)。
 * 列(2026-08 の実ファイルで確認。Shift_JIS):
 *   請求アカウント / 請求アカウント名称 / 請求月 / 利用月 / 部署名 / **ご利用番号** / 関連契約番号 /
 *   通話種別名称 / 着信番号 / **通話開始年月日**(YYYY/MM/DD) / 通話開始時間 / 通話先電話番号 /
 *   通話先地域 / **通話時間**(秒) / **通話料金**(円・小数あり)
 *
 * **顧客の電話番号(通話先電話番号)は保存しない**(CLAUDE.md セキュリティ)。
 * 集計して「会社 × 日 の 通話時間・通話料・件数」だけを返す。
 */
import { companyOfNumber, type CallNumber } from '@core/masters/settingsPayRepo';
import type { DateStr } from '@core/lib/types';

export const CALL_CHARGE_COLUMNS = {
  number: 'ご利用番号',
  date: '通話開始年月日',
  sec: '通話時間',
  yen: '通話料金',
} as const;

/** 番号ごとの合計(用途の内訳を出すため。2026-09-28) */
export type CallChargeByNumber = {
  number: string;
  company_id: string | null;
  calls: number;
  sec: number;
  yen: number;
};

/** 集計結果の1行(**発信番号 × 日**。会社別・用途別・日別のどれにも使える) */
export type CallChargeRow = {
  /** 発信番号(数字だけ) */
  number: string;
  /** 会社ID。番号がどの会社か分からないときは null */
  company_id: string | null;
  date: DateStr;
  /** 通話回数 */
  calls: number;
  /** 通話時間(秒) */
  sec: number;
  /** 通話料(円。小数のまま足して最後に丸める) */
  yen: number;
};

export type CallChargeResult = {
  rows: CallChargeRow[];
  /** 発信番号ごとの合計(用途別の内訳に使う) */
  by_number: CallChargeByNumber[];
  /** 読んだ明細の行数 */
  total_rows: number;
  /** 合計(秒・円) */
  total_sec: number;
  total_yen: number;
  /** 設定に無い(会社が決まらない)発信番号と、その件数・金額 */
  unknown_numbers: { number: string; calls: number; yen: number }[];
  /** 利用月(CSVの「利用月」。複数あれば複数) */
  months: string[];
  /** 読めなかった行(日付や数値の形式が違う) */
  skipped: number;
};

/** "2026/08/05" → "2026-08-05"。読めなければ null */
export function toDateStr(s: string): DateStr | null {
  const m = (s ?? '').trim().match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (!m) return null;
  return `${m[1]}-${m[2]!.padStart(2, '0')}-${m[3]!.padStart(2, '0')}`;
}

function num(s: string): number {
  const n = Number((s ?? '').replace(/[,\s]/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

/**
 * 明細(ヘッダ付きの行の配列)を 会社 × 日 に集計する。
 * `month` を渡すと、その月(YYYY-MM)の通話だけを数える
 */
export function aggregateCallCharges(rows: Record<string, string>[], numbers: CallNumber[], month?: string): CallChargeResult {
  const acc = new Map<string, CallChargeRow>();
  const perNumber = new Map<string, CallChargeByNumber>();
  const unknown = new Map<string, { number: string; calls: number; yen: number }>();
  const months = new Set<string>();
  let totalSec = 0;
  let totalYen = 0;
  let skipped = 0;
  let used = 0;
  for (const r of rows) {
    const number = (r[CALL_CHARGE_COLUMNS.number] ?? '').trim();
    const date = toDateStr(r[CALL_CHARGE_COLUMNS.date] ?? '');
    const sec = num(r[CALL_CHARGE_COLUMNS.sec] ?? '');
    const yen = num(r[CALL_CHARGE_COLUMNS.yen] ?? '');
    if (!number || !date || !Number.isFinite(sec) || !Number.isFinite(yen)) {
      skipped++;
      continue;
    }
    if (month && date.slice(0, 7) !== month) continue;
    months.add(date.slice(0, 7));
    used++;
    totalSec += sec;
    totalYen += yen;
    const cid = companyOfNumber(numbers, number, date);
    if (!cid) {
      const u = unknown.get(number) ?? { number, calls: 0, yen: 0 };
      u.calls++;
      u.yen += yen;
      unknown.set(number, u);
    }
    const nkey = number.replace(/[^0-9]/g, '');
    const pn = perNumber.get(nkey) ?? { number, company_id: cid, calls: 0, sec: 0, yen: 0 };
    pn.calls++;
    pn.sec += sec;
    pn.yen += yen;
    if (cid) pn.company_id = cid;
    perNumber.set(nkey, pn);
    const key = `${nkey}|${date}`;
    const cur = acc.get(key) ?? { number: nkey, company_id: cid, date, calls: 0, sec: 0, yen: 0 };
    cur.calls++;
    cur.sec += sec;
    cur.yen += yen;
    acc.set(key, cur);
  }
  const list = [...acc.values()].map((r) => ({ ...r, yen: Math.round(r.yen * 100) / 100 }));
  list.sort((a, b) => (a.date === b.date ? a.number.localeCompare(b.number) : a.date < b.date ? -1 : 1));
  return {
    rows: list,
    by_number: [...perNumber.values()].map((x) => ({ ...x, yen: Math.round(x.yen * 100) / 100 })).sort((a, b) => b.yen - a.yen),
    total_rows: used,
    total_sec: totalSec,
    total_yen: Math.round(totalYen * 100) / 100,
    unknown_numbers: [...unknown.values()].sort((a, b) => b.yen - a.yen).map((u) => ({ ...u, yen: Math.round(u.yen * 100) / 100 })),
    months: [...months].sort(),
    skipped,
  };
}

/** 会社ごとの合計(画面の表示用) */
export function byCompany(rows: CallChargeRow[]): { company_id: string | null; calls: number; sec: number; yen: number }[] {
  const acc = new Map<string, { company_id: string | null; calls: number; sec: number; yen: number }>();
  for (const r of rows) {
    const key = r.company_id ?? '';
    const cur = acc.get(key) ?? { company_id: r.company_id, calls: 0, sec: 0, yen: 0 };
    cur.calls += r.calls;
    cur.sec += r.sec;
    cur.yen += r.yen;
    acc.set(key, cur);
  }
  return [...acc.values()].map((x) => ({ ...x, yen: Math.round(x.yen * 100) / 100 })).sort((a, b) => b.yen - a.yen);
}
